import React, { FC } from 'react';
import Truncate from 'react-truncate-markup';

import usePageType from 'helpers/usePageType';
import { CardSchema, ECardExtractionOptions } from 'constants/graphqlTypes';
import { getCardMediaTypeLabelId, getCardTitleId } from 'constants/aqa/card';
import useCardContext from 'helpers/useCardProvider';
import { isVideoCard } from 'helpers/isVideoCard';
import { StyledBio } from 'components/UI/Highlight/styled';

import { getBrandedIcon, getCardTagCaption } from '../helpers/helpers';
import { StyledContent, StyledTag, StyledTitleContainer, StyledTweet, StyledSaved } from './styled';

interface IContentProps {
  card: CardSchema;
  bigTitle?: boolean;
  hugeTitle?: boolean;
  hasPaddings?: boolean;
  dataTestId: string;
  isImageVertical?: boolean;
  isTitleView?: boolean;
}

// Custom ellipsis component with 0.5 opacity
const CustomEllipsis: FC = () => <span style={{ opacity: 0.5 }}>...</span>;

const Content: FC<IContentProps> = ({
  card,
  bigTitle,
  hugeTitle,
  hasPaddings,
  dataTestId,
  isImageVertical,
  isTitleView,
}) => {
  const { mediaTagNames, title, displayTitle, extractionStatus, externalCreators, sourceName, isSavedByMe } = card;
  const tagName = mediaTagNames?.[0] ?? card.mediaTags?.items?.[0]?.name;
  const { creatorUsername, creatorName } = externalCreators?.items?.[0] ?? {};
  const currentTitle = displayTitle ?? title;

  const isVideo = isVideoCard(card);
  const isTweet = tagName === 'tweets';
  const Icon = getBrandedIcon(sourceName);
  const isVerticalBrandedVideoCard = isVideo && !!Icon && isImageVertical;
  const hasEllipsis = !!Icon && currentTitle && currentTitle?.length > 180 && !isTitleView;

  const tag = !tagName && extractionStatus !== ECardExtractionOptions.Complete ? 'Loading' : tagName;
  const { cardHeader } = useCardContext() ?? {};
  const { isAddThought } = usePageType();

  const truncatedTitle = (hasEllipsis
    ? currentTitle?.slice(0, currentTitle?.lastIndexOf(' ', 180))
    : currentTitle || 'Generating title'
  )?.concat(hasEllipsis ? ' [...]' : '');

  const editedName = sourceName === 'mastodon' ? creatorName?.split('&')[0] : creatorName;

  return (
    <StyledContent hasPaddings={hasPaddings}>
      {(cardHeader || tag || !!Icon) && (
        <StyledTag data-testid={getCardMediaTypeLabelId(dataTestId)} disableCapitalize>
          {getCardTagCaption({ isVideo, isTweet, cardHeader, tag })}
        </StyledTag>
      )}
      {!!Icon && (
        <StyledTweet>
          <Icon viewBox="0 0 24 24" height="13" width="13" />
          {editedName}
          {creatorUsername && <StyledBio>@{creatorUsername.replace('@', '')}</StyledBio>}
        </StyledTweet>
      )}
      <StyledTitleContainer
        isBig={bigTitle && !isVerticalBrandedVideoCard}
        isHuge={hugeTitle && !isVerticalBrandedVideoCard}
      >
        <Truncate lines={4} ellipsis={<CustomEllipsis />}>
          <span data-testid={getCardTitleId(dataTestId)}>{truncatedTitle}</span>
        </Truncate>
      </StyledTitleContainer>
      {isSavedByMe && !isAddThought && <StyledSaved>Saved pick</StyledSaved>}
    </StyledContent>
  );
};

export default Content;
